import jjaLogo from "./assets/jja-logo.png";
import Projects from "./Projects";
import { useCallback, useEffect, useState } from "react";
import emailjs from "@emailjs/browser";
import Particles from "react-tsparticles";
import { loadFull } from "tsparticles";

const EMAILJS_SERVICE_ID = "service_uqsmk5o";
const EMAILJS_TEMPLATE_ID = "template_e335ikz";
const EMAILJS_PUBLIC_KEY = "w6vTeUBcm4uQOKZhB";

const ConnectedDotsBackground = () => {
  // Load the full tsparticles bundle so shapes/links work
  const particlesInit = useCallback(async (engine) => {
    await loadFull(engine);
  }, []);

  return (
    <Particles
      className="absolute inset-0 z-0 pointer-events-none"
      style={{ height: "100%" }} // ensure canvas fills the section
      init={particlesInit}
      options={{
        fullScreen: true,
        background: {
          color: "transparent",
        },
        fpsLimit: 60,
        particles: {
          number: {
            value: 60,
            density: {
              enable: true,
              area: 800,
            },
          },
          color: {
            value: "#ffffff",
          },
          links: {
            enable: true,
            distance: 140,
            color: "#ffffff",
            opacity: 0.4,
            width: 1,
          },
          move: {
            enable: true,
            speed: 1.2,
            direction: "none",
            random: false,
            straight: false,
            outModes: {
              default: "bounce",
            },
          },
          opacity: {
            value: 0.7,
          },
          size: {
            value: 3,
            random: true,
          },
        },
        interactivity: {
          events: {
            onHover: {
              enable: true,
              mode: "repulse", // move away on hover
            },
            resize: true,
          },
          modes: {
            repulse: {
              distance: 120,
              duration: 0.4,
            },
          },
        },
        detectRetina: true,
      }}
    />
  );
};

export default function ITSolutionsLandingPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactStatus, setContactStatus] = useState({
    type: "",
    message: "",
  });

  useEffect(() => {
    const exitTimer = setTimeout(() => setIsExiting(true), 2500);
    const hideTimer = setTimeout(() => setIsLoading(false), 3000);
    return () => {
      clearTimeout(exitTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  // const services = [
  //   {
  //     title: "Managed IT Services",
  //     description:
  //       "JJA delivers high-quality, proactive IT monitoring, maintenance, and support—keeping your systems secure, optimized, and running smoothly 24/7.",
  //     tag: "Support & Reliability",
  //   },
  //   {
  //     title: "IT Solutions",
  //     description:
  //       "JJA provides high-quality IT solutions and digital experiences—modern websites, applications, and smart integrations built to match your business goals.",
  //     tag: "Digital Experiences",
  //   },
  //   // {
  //   //   title: "Cloud & Infrastructure",
  //   //   description:
  //   //     "Migrate, optimize, and secure your infrastructure on AWS, Azure, or Google Cloud.",
  //   //   tag: "Scalable Cloud",
  //   // },
  //   // {
  //   //   title: "Cybersecurity & Compliance",
  //   //   description:
  //   //     "Protect your data, your people, and your brand with end-to-end security solutions.",
  //   //   tag: "Security First",
  //   // },
  // ];
  const services = [
    {
      title: "Custom Software Development",
      tag: "Business Automation",
      description:
        "We build tailored software solutions that streamline operations, improve efficiency, and support your business growth.",
      items: [
        "Web-based system development",
        "Company management systems",
        "Mobile app development",
        "Business automation systems",
        "Database system design",
        "API integrations",
      ],
    },
    {
      title: "Website & E-Commerce Development",
      tag: "Digital Presence",
      description:
        "We create modern, responsive, and professional websites that strengthen your brand and help convert visitors into customers.",
      items: [
        "Company websites",
        "E-commerce websites",
        "Booking websites",
        "Portfolio websites",
        "Landing pages",
      ],
    },
    {
      title: "Managed IT Services",
      tag: "Support & Reliability",
      description:
        "We provide proactive maintenance, monitoring, and technical support to keep your systems secure, stable, and running smoothly.",
      items: [
        "System maintenance and support",
        "Technical troubleshooting",
        "Remote and on-site assistance",
        "Preventive maintenance",
      ],
    },
    {
      title: "IT Infrastructure & Network Setup",
      tag: "Infrastructure",
      description:
        "We design and deploy reliable IT environments for offices and businesses, from network setup to server and storage solutions.",
      items: [
        "Office network setup",
        "Router & switch configuration",
        "WiFi mesh deployment",
        "NAS setup",
        "Server installation",
        "VLAN configuration",
        "Firewall setup",
      ],
    },
    {
      title: "Cloud Hosting & Data Backup",
      tag: "Scalable Systems",
      description:
        "We help businesses deploy secure hosting, hybrid infrastructure, and dependable backup solutions for business continuity.",
      items: [
        "Local server hosting",
        "Cloud hosting",
        "Hybrid server setup",
        "Backup services",
        "Automated backups",
        "Cloud backup integration",
        "Disaster recovery planning",
      ],
    },
    {
      title: "Cybersecurity & System Integration",
      tag: "Security First",
      description:
        "We protect your systems and connect your technologies to improve security, visibility, and operational efficiency.",
      items: [
        "Firewall configuration",
        "Access control",
        "Server security",
        "Network monitoring",
        "Security audits",
        "System integration",
      ],
    },
    {
      title: "CCTV & Surveillance Solutions",
      tag: "Security Monitoring",
      description:
        "We install and maintain surveillance systems that help businesses improve safety, visibility, and remote monitoring capabilities.",
      items: [
        "CCTV installation",
        "Remote viewing setup",
        "Surveillance storage server",
        "Maintenance and cleaning",
      ],
    },
    {
      title: "Computer Maintenance & Repair",
      tag: "Technical Services",
      description:
        "We diagnose, repair, and optimize computers and laptops to reduce downtime and keep your devices performing at their best.",
      items: [
        "Computer troubleshooting",
        "OS installation (Windows / Linux)",
        "Virus and malware removal",
        "Computer formatting and cleanup",
        "Hardware repair and replacement",
        "RAM / SSD upgrade",
        "Laptop screen replacement",
        "Printer troubleshooting",
        "Network troubleshooting",
      ],
    },
    {
      title: "IT Consultancy",
      tag: "Strategic Guidance",
      description:
        "We help businesses make better technology decisions through expert advice on systems, infrastructure, security, and digital transformation.",
      items: [
        "Technology planning",
        "System recommendations",
        "Infrastructure assessment",
        "Digital transformation guidance",
      ],
    },
  ];

  const steps = [
    {
      label: "01",
      title: "Discover",
      text: "We start with a deep dive into your operations, tech stack, and goals.",
    },
    {
      label: "02",
      title: "Design",
      text: "We architect solutions that align with your processes and budget.",
    },
    {
      label: "03",
      title: "Deliver",
      text: "We implement, iterate, and support you long-term as you scale.",
    },
  ];

  const techStack = [
    {
      name: "HTML5",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg",
    },
    {
      name: "CSS3",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg",
    },
    {
      name: "JavaScript",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg",
    },
    {
      name: "Java",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg",
    },
    {
      name: "C#",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/csharp/csharp-original.svg",
    },
    { name: "VB.NET", logo: null },
    {
      name: ".NET",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dot-net/dot-net-original.svg",
    },
    {
      name: "Node.js",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
    },
    {
      name: "React",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
    },
    {
      name: "Spring Boot",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg",
    },
    {
      name: "Laravel",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/laravel/laravel-original.svg",
    },
    {
      name: "Gatsby",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/gatsby/gatsby-original.svg",
    },
    {
      name: "Azure",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg",
    },
    {
      name: "Git",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg",
    },
    {
      name: "GitHub",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg",
    },
    {
      name: "Google Cloud",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg",
    },
    {
      name: "XAMPP",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apache/apache-original.svg",
    },
    {
      name: "MySQL Workbench",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg",
    },
    {
      name: "Firebase",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg",
    },
    {
      name: "Tailwind CSS",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg",
    },
    {
      name: "Bootstrap",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bootstrap/bootstrap-original.svg",
    },
    {
      name: "Figma",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg",
    },
    {
      name: "Material UI",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/materialui/materialui-original.svg",
    },
  ];

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setContactStatus({ type: "", message: "" });
    setIsSubmitting(true);
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const name = formData.get("user_name")?.toString().trim() || "";
    const email = formData.get("user_email")?.toString().trim() || "";
    const projectType = formData.get("project_type")?.toString().trim() || "";
    const message = formData.get("user_message")?.toString().trim() || "";

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          user_name: name,
          from_name: name,
          name,
          user_email: email,
          from_email: email,
          reply_to: email,
          email,
          project_type: projectType,
          user_message: message,
          message,
          to_email: "jja.solutions22@gmail.com",
        },
        {
          publicKey: EMAILJS_PUBLIC_KEY,
        },
      );

      setContactStatus({
        type: "success",
        message: "Inquiry sent successfully. We will reply to your email soon.",
      });
      formEl.reset();
      setTimeout(() => {
        setContactStatus({ type: "", message: "" });
      }, 5000);
    } catch (error) {
      setContactStatus({
        type: "error",
        message: `Message not sent (${error?.text || error?.message || "service error"})`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07141f] text-slate-50">
      <ConnectedDotsBackground />

      {/* Gradient background accent */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-40 top-[-10rem] h-96 w-96 rounded-full bg-[#2E7EAE]/25 blur-3xl" />
        <div className="absolute -right-20 bottom-[-8rem] h-[28rem] w-[28rem] rounded-full bg-[#205C7A]/25 blur-3xl" />
      </div>

      {/* HEADER */}
      <header className="sticky top-0 z-20 border-b border-white/5 bg-[#07141f]/90 backdrop-blur main-pop-in">
        {!isLoading && (
          <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3">
              <img
                src={jjaLogo}
                alt="JJA Software & IT Services logo"
                className="h-9 w-auto"
              />
              <div>
                <div className="text-sm font-semibold tracking-wide text-slate-50">
                  JJA Software & IT Services
                </div>
                <p className="text-xs text-slate-300">
                  Software Development • IT Services
                </p>
              </div>
            </div>

            <nav className="hidden items-center gap-8 text-sm text-slate-300 md:flex">
              <a href="#projects" className="transition hover:text-[#a8cdef]">Projects</a>
              <a href="#services" className="transition hover:text-[#a8cdef]">
                Services
              </a>
              <a href="#process" className="transition hover:text-[#a8cdef]">
                Process
              </a>
              <a href="#tech-stack" className="transition hover:text-[#a8cdef]">
                Tech Stack
              </a>
              <a href="#about" className="transition hover:text-[#a8cdef]">
                About Us
              </a>
              <a href="#contact" className="transition hover:text-[#a8cdef]">
                Contact
              </a>
            </nav>
          </div>
        )}
      </header>

      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center overflow-hidden">
          <img
            src={jjaLogo}
            alt="JJA Software & IT Services logo"
            className={`relative z-10 h-32 w-auto ${isExiting ? "logo-pop-out" : "logo-pop-in"}`}
          />
        </div>
      )}

      {/* MAIN */}
      {!isLoading && (
        <main className="mx-auto max-w-6xl px-6 pb-20 pt-10 main-pop-in">
          {/* HERO */}

          <section className="grid gap-10 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] md:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-slate-200">
                <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Helping businesses operate smarter & grow online
              </span>
              <h1 className="mt-5 text-balance text-3xl font-semibold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
                Modern IT solutions that
                <span className="bg-gradient-to-r from-[#a8cdef] via-[#2E7EAE] to-[#205C7A] bg-clip-text text-transparent">
                  {" "}
                  optimize operations
                </span>{" "}
                and elevate your digital presence.
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
                From infrastructure and cloud to custom websites and
                applications, we design and build technology that actually
                supports the way your business works.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a
                  href="#contact"
                  className="rounded-full bg-gradient-to-r from-[#2E7EAE] to-[#205C7A] px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[#2E7EAE]/50 transition hover:brightness-110"
                >
                  Book a strategy call
                </a>
                <a
                  href="#services"
                  className="text-sm font-medium text-slate-200 underline-offset-4 hover:underline"
                >
                  View services
                </a>
              </div>

              <div className="mt-8 grid max-w-xl grid-cols-3 gap-4 text-xs text-slate-300 sm:text-sm">
                <div>
                  <div className="text-lg font-semibold text-slate-50">
                    99.9%
                  </div>
                  <p className="text-slate-400">
                    Uptime on managed infrastructure solutions.
                  </p>
                </div>
                <div>
                  <div className="text-lg font-semibold text-slate-50">2x</div>
                  <p className="text-slate-400">
                    Average improvement in response time after optimization.
                  </p>
                </div>
                <div>
                  <div className="text-lg font-semibold text-slate-50">
                    15+{" "}
                  </div>
                  <p className="text-slate-400">
                    SMBs supported across cloud, web, and IT operations.
                  </p>
                </div>
              </div>
            </div>

            {/* HERO CARD */}
            {/* <div className="md:pl-6">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900/60 p-5 shadow-xl shadow-slate-900/80">
              <div className="absolute right-0 top-0 h-32 w-32 translate-x-10 -translate-y-10 rounded-full bg-[#2E7EAE]/25 blur-3xl" />
              <div className="absolute bottom-0 left-0 h-24 w-24 -translate-x-10 translate-y-10 rounded-full bg-[#205C7A]/25 blur-3xl" />

              <div className="relative flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#a8cdef]">
                    Live Systems Overview
                  </p>
                  <h2 className="mt-1 text-lg font-semibold text-slate-50">
                    Client Infrastructure Health
                  </h2>
                </div>
                <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-[11px] font-medium text-emerald-300">
                  All systems operational
                </span>
              </div>

              <div className="relative mt-5 space-y-4 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Web Application</span>
                  <span className="text-emerald-300">Operational</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-800">
                  <div className="h-1.5 w-11/12 rounded-full bg-gradient-to-r from-emerald-400 to-[#2E7EAE]" />
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <span className="text-slate-300">API & Integrations</span>
                  <span className="text-amber-300">Optimizing</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-800">
                  <div className="h-1.5 w-3/4 rounded-full bg-gradient-to-r from-amber-300 to-orange-400" />
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <span className="text-slate-300">Security & Backups</span>
                  <span className="text-sky-300">Protected</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-800">
                  <div className="h-1.5 w-full rounded-full bg-gradient-to-r from-sky-400 to-[#205C7A]" />
                </div>
              </div>

              <div className="relative mt-6 rounded-2xl border border-white/10 bg-slate-900/80 p-4 text-xs text-slate-300">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                      Sample win
                    </p>
                    <p className="text-sm font-medium text-slate-50">
                      Load time cut from 4.3s to 1.2s
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] text-emerald-300">+62% conversion</p>
                    <p className="text-[11px] text-slate-400">
                      after performance tuning
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div> */}
          </section>

          {/* SERVICES */}
          <section
            id="services"
            className="mt-16 border-t border-white/5 pt-12"
          >
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#a8cdef]">
                  Services
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-50">
                  What we can help you with
                </h2>
              </div>
              <p className="max-w-md text-sm text-slate-300">
                Whether you need a reliable partner to manage your IT or a team
                to design and ship your next digital product, we craft solutions
                that fit the way you actually work.
              </p>
            </div>

            <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {services.map((service) => (
                <div
                  key={service.title}
                  className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 p-6 transition duration-300 hover:-translate-y-1 hover:border-[#2E7EAE]/70 hover:bg-slate-900"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2E7EAE]/0 via-[#2E7EAE]/0 to-[#205C7A]/0 opacity-0 transition group-hover:opacity-20" />

                  <div className="relative flex h-full flex-col">
                    <span className="inline-flex w-fit rounded-full bg-[#2E7EAE]/15 px-2.5 py-1 text-[11px] font-medium text-[#a8cdef]">
                      {service.tag}
                    </span>

                    <h3 className="mt-4 text-lg font-semibold text-slate-50">
                      {service.title}
                    </h3>

                    <p className="mt-3 text-sm leading-relaxed text-slate-300">
                      {service.description}
                    </p>

                    <ul className="mt-4 space-y-2 text-sm text-slate-400">
                      {service.items.slice(0, 5).map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="mt-1 text-[#7ec8f8]">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    {/* <button className="mt-6 inline-flex w-fit items-center gap-2 text-sm font-medium text-[#a8cdef] transition group-hover:text-white">
                      View service details
                      <span className="text-base leading-none">→</span>
                    </button> */}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* TECH STACK */}
          <section
            id="tech-stack"
            className="mt-16 border-t border-white/5 pt-12"
          >
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#a8cdef]">
                  Skills
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-50">
                  Technology stack we work with
                </h2>
              </div>
              <p className="max-w-md text-sm text-slate-300">
                From frontend frameworks to cloud and backend platforms, we use
                proven tools to build reliable products and scalable systems.
              </p>
            </div>

            <div className="mt-8 rounded-3xl border border-white/10 bg-slate-900/60 p-5 sm:p-6">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {techStack.map((tech) => (
                  <div
                    key={tech.name}
                    className="flex items-center gap-2 rounded-xl border border-white/10 bg-slate-900/70 px-3 py-2.5 text-xs font-medium text-slate-200 transition hover:border-[#2E7EAE]/60 hover:text-white"
                  >
                    {tech.logo ? (
                      <img
                        src={tech.logo}
                        alt={`${tech.name} logo`}
                        className="h-5 w-5 object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/10 text-[10px] font-semibold text-slate-100">
                        {tech.name
                          .split(/\s+/)
                          .map((part) => part[0])
                          .join("")
                          .replace(".", "")
                          .slice(0, 2)
                          .toUpperCase()}
                      </span>
                    )}
                    <span className="truncate">{tech.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <Projects />

          {/* PROCESS */}
          <section id="process" className="mt-16 border-t border-white/5 pt-12">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="max-w-md">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#a8cdef]">
                  Process
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-50">
                  A simple, transparent way of working.
                </h2>
                <p className="mt-3 text-sm text-slate-300">
                  No black boxes. You get clear communication, technical
                  documentation, and measurable outcomes at every step.
                </p>
              </div>
              <div className="grid flex-1 gap-4 sm:grid-cols-3">
                {steps.map((step) => (
                  <div
                    key={step.label}
                    className="rounded-2xl border border-white/10 bg-slate-900/70 p-4 text-xs text-slate-300"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-[#a8cdef]">
                        {step.label}
                      </span>
                      <span className="h-1 w-8 rounded-full bg-gradient-to-r from-[#2E7EAE] to-[#205C7A]" />
                    </div>
                    <h3 className="mt-3 text-sm font-semibold text-slate-50">
                      {step.title}
                    </h3>
                    <p className="mt-1 leading-relaxed">{step.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ABOUT & CONTACT */}
          <section
            id="about"
            className="relative mt-16 border-t border-white/5 pt-12 overflow-hidden"
          >
            <div className="relative z-10 grid gap-10 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#a8cdef]">
                  About Us
                </p>

                <h2 className="mt-2 text-2xl font-semibold text-slate-50">
                  Why businesses choose JJA Software & IT Services
                </h2>

                <p className="mt-3 text-sm text-slate-300">
                  JJA Software & IT Services is a technology solutions provider
                  based in Cebu City, Philippines. We specialize in delivering
                  reliable software development, IT infrastructure, and
                  technical support services designed to help businesses operate
                  more efficiently and securely. Our mission is to empower
                  organizations through modern technology solutions that
                  simplify operations, improve productivity, and support
                  long-term growth.
                </p>

                <p className="mt-3 text-sm text-slate-300">
                  With a strong focus on innovation, reliability, and client
                  partnership, JJA works closely with businesses to design and
                  implement systems that solve real operational challenges. From
                  custom business applications and websites to network
                  infrastructure, cloud deployment, and technical support, we
                  provide end-to-end IT services that businesses can depend on.
                </p>

                <div className="mt-6 grid gap-4 text-sm text-slate-200 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      For growing businesses
                    </p>
                    <p className="mt-2 text-sm text-slate-200">
                      We develop custom systems that automate workflows, reduce
                      manual processes, and provide better visibility into
                      business operations.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      For modern digital presence
                    </p>
                    <p className="mt-2 text-sm text-slate-200">
                      From professional websites to integrated platforms, we
                      help businesses build strong digital foundations that
                      support marketing, sales, and customer engagement.
                    </p>
                  </div>
                </div>
              </div>

              <div
                id="contact"
                className="rounded-3xl border border-white/10 bg-slate-900/70 p-6"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#a8cdef]">
                  Contact
                </p>
                <h3 className="mt-2 text-lg font-semibold text-slate-50">
                  Tell us about your project
                </h3>
                <p className="mt-2 text-xs text-slate-300">
                  Share a bit about your current challenges and ideal outcome.
                  We’ll reply with a free, no-pressure technical recommendation.
                </p>

                <form
                  id="contact-form"
                  onSubmit={handleContactSubmit}
                  className="mt-4 space-y-3 text-xs"
                >
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-slate-200">
                      Name
                    </label>
                    <input
                      name="user_name"
                      required
                      className="w-full rounded-xl border border-white/10 bg-slate-900/80 px-3 py-2 text-xs text-slate-50 outline-none ring-[#2E7EAE]/40 placeholder:text-slate-500 focus:border-[#2E7EAE] focus:ring-2"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-slate-200">
                      Work email
                    </label>
                    <input
                      name="user_email"
                      required
                      className="w-full rounded-xl border border-white/10 bg-slate-900/80 px-3 py-2 text-xs text-slate-50 outline-none ring-[#2E7EAE]/40 placeholder:text-slate-500 focus:border-[#2E7EAE] focus:ring-2"
                      placeholder="you@company.com"
                      type="email"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-slate-200">
                      Project type
                    </label>
                    <select
                      name="project_type"
                      required
                      defaultValue=""
                      className="w-full rounded-xl border border-white/10 bg-slate-900/80 px-3 py-2 text-xs text-slate-50 outline-none focus:border-[#2E7EAE] focus:ring-2 focus:ring-[#2E7EAE]/40"
                    >
                      <option value="" disabled>
                        Select project type
                      </option>
                      <option>Custom Website Development</option>
                      <option>Web Application Development</option>
                      <option>Mobile App Development</option>
                      <option>Managed IT Services</option>
                      <option>Cloud Setup & Migration</option>
                      <option>Cybersecurity & Compliance</option>
                      <option>System Integration & Automation</option>
                      <option>Ongoing Maintenance & Support</option>
                      <option>IT Consulting</option>
                      <option>Other / Not sure yet</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-slate-200">
                      What are you trying to achieve?
                    </label>
                    <textarea
                      name="user_message"
                      required
                      rows={3}
                      className="w-full rounded-xl border border-white/10 bg-slate-900/80 px-3 py-2 text-xs text-slate-50 outline-none ring-[#2E7EAE]/40 placeholder:text-slate-500 focus:border-[#2E7EAE] focus:ring-2"
                      placeholder="Share a few details about your current setup, challenges, and goals."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#2E7EAE] to-[#205C7A] px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-white shadow-lg shadow-[#2E7EAE]/50 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-80"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        Sending...
                      </>
                    ) : (
                      "Submit inquiry"
                    )}
                  </button>

                  {contactStatus.message ? (
                    <p
                      id="contact-message"
                      className={`text-[11px] ${
                        contactStatus.type === "success"
                          ? "text-emerald-300"
                          : "text-rose-300"
                      }`}
                    >
                      {contactStatus.message}
                    </p>
                  ) : null}

                  <p className="mt-2 text-[10px] text-slate-500">
                    By submitting, you agree to receive an email response from
                    JJA Software & IT Services. No spam, ever.
                  </p>
                </form>
              </div>
            </div>
          </section>
        </main>
      )}
      {/* FOOTER */}
      {!isLoading && (
        <footer className="border-t border-white/5 bg-[#07141f]/95 main-pop-in">
          <div className="mx-auto flex max-w-6xl flex-col gap-3 px-6 py-5 text-[11px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <p>
              © {new Date().getFullYear()} JJA Software & IT Services. All
              rights reserved.
            </p>
            <p className="flex flex-wrap gap-3">
              <span>Software & IT Solutions</span>
              <span className="hidden text-slate-600 sm:inline">•</span>
              <span>Built with modern web technologies.</span>
            </p>
          </div>
        </footer>
      )}
    </div>
  );
}
